import React, { useState, useEffect, useMemo } from 'react';
import apiService from '../services/api';
import {
  BeakerIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  SparklesIcon,
  UserIcon,
  AcademicCapIcon,
  ClipboardDocumentListIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import ResponseRanker from '../components/Evaluation/ResponseRanker';
import type { EvalCase, EvalTurn } from '../types';

interface LogicalCase {
  sourceType: 'gold' | 'synthetic';
  sampleId: number;
  title: string;
}

type TrainVariant = 'current' | 'minimal' | 'none';

function detectTrainVariant(modelName: string): TrainVariant {
  if (modelName.endsWith(' minimal')) return 'minimal';
  if (modelName.endsWith(' none')) return 'none';
  return 'current';
}

function baseModelKey(modelName: string): string {
  return modelName
    .replace(/\s+(minimal|none)$/i, '')
    .replace(/\s+v\d+$/i, '')
    .trim();
}

interface VariantBlock {
  response: string;
  id: string;
  turnId: string;
  modelName: string;
}

function getModelColor(modelName: string): { bg: string; text: string; border: string } {
  if (modelName.includes('GPT')) return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
  if (modelName.includes('Gemini')) return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
  if (modelName.includes('Claude')) return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
  if (modelName.includes('MedGemma')) return { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' };
  if (modelName.includes('Qwen')) return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
  return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' };
}

const RANKING_SECTION_ENABLED = false; // set true to show "Rank responses" and model selection for ranking

const EvaluationPage: React.FC = () => {
  const [cases, setCases] = useState<EvalCase[]>([]);
  const [logicalCases, setLogicalCases] = useState<LogicalCase[]>([]);
  const [selectedLogical, setSelectedLogical] = useState<LogicalCase | null>(null);
  const [variants, setVariants] = useState<EvalCase[]>([]);
  const [selectedTurnIdx, setSelectedTurnIdx] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCaseLoading, setIsCaseLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'gold' | 'synthetic'>('all');
  const [rubricSuggestions, setRubricSuggestions] = useState('');
  const [isSavingFeedback, setIsSavingFeedback] = useState(false);
  const [feedbackSaveMessage, setFeedbackSaveMessage] = useState<string | null>(null);
  const [savedTurns, setSavedTurns] = useState<Set<string>>(new Set());
  const [visibleBases, setVisibleBases] = useState<Set<string>>(new Set());
  const [showModelList, setShowModelList] = useState(false);
  const [rankingByTurnId, setRankingByTurnId] = useState<Record<string, { best: { id: string; comment: string }[]; worst: { id: string; comment: string }[] }>>({});
  const [expandedComparisonId, setExpandedComparisonId] = useState<string | null>(null);
  const [showSystemPrompts, setShowSystemPrompts] = useState(false);
  const [expandedSystemPromptId, setExpandedSystemPromptId] = useState<string | null>(null);

  useEffect(() => {
    loadCases();
  }, [sourceFilter]);

  const loadCases = async () => {
    try {
      setIsLoading(true);
      setError('');
      const sourceType = sourceFilter === 'all' ? undefined : sourceFilter;
      const result = await apiService.getEvalCases(sourceType, 1, 500);
      setCases(result.cases);
      const grouped = groupByLogicalCase(result.cases);
      setLogicalCases(grouped);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load evaluation cases');
    } finally {
      setIsLoading(false);
    }
  };

  function groupByLogicalCase(caseList: EvalCase[]): LogicalCase[] {
    const map = new Map<string, LogicalCase>();
    caseList.forEach((c) => {
      const key = `${c.sourceType}-${c.sampleId}`;
      if (!map.has(key)) {
        map.set(key, {
          sourceType: c.sourceType as 'gold' | 'synthetic',
          sampleId: c.sampleId,
          title: c.title || `Case #${c.sampleId + 1}`,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      if (a.sourceType !== b.sourceType) return a.sourceType === 'gold' ? -1 : 1;
      return a.sampleId - b.sampleId;
    });
  }

  const loadLogicalCase = async (logical: LogicalCase) => {
    try {
      setIsCaseLoading(true);
      setSelectedLogical(logical);
      setSelectedTurnIdx(null);
      setRubricSuggestions('');
      setFeedbackSaveMessage(null);
      const { cases: variantCases } = await apiService.getEvalCasesByLogicalCase(logical.sourceType, logical.sampleId);
      setVariants(variantCases);

      if (variantCases.length > 0) {
        try {
          const feedback = await apiService.getEvalCaseFeedback(variantCases[0].id);
          setRubricSuggestions(feedback.rubricSuggestions || '');
        } catch {
          // ignore
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load case');
    } finally {
      setIsCaseLoading(false);
    }
  };

  const saveFeedback = async () => {
    if (variants.length === 0) return;
    try {
      setIsSavingFeedback(true);
      setFeedbackSaveMessage(null);
      await apiService.submitEvalCaseFeedback(variants[0].id, {
        rubricSuggestions: rubricSuggestions.trim() || undefined,
      });
      setFeedbackSaveMessage('Saved!');
      setTimeout(() => setFeedbackSaveMessage(null), 2000);
    } catch {
      setFeedbackSaveMessage('Failed to save');
    } finally {
      setIsSavingFeedback(false);
    }
  };

  const handleRankingSaved = (turnId: string) => {
    setSavedTurns((prev) => new Set(prev).add(turnId));
  };

  const caseMinimalNone = useMemo(() => variants.find((c) => c.promptVariant === 'minimal_none') ?? null, [variants]);
  const displayCase = caseMinimalNone ?? variants[0] ?? null;

  const turns = displayCase?.turns ?? [];
  const currentTurn: EvalTurn | null =
    selectedTurnIdx != null ? turns.find((t) => t.turnIdx === selectedTurnIdx) ?? null : null;

  const previousTurns = useMemo(() => {
    if (selectedTurnIdx == null) return [];
    return turns.filter((t) => t.turnIdx < selectedTurnIdx);
  }, [turns, selectedTurnIdx]);

  // One card per base model: v4 + minimal + none in the same card (with id/turnId for ranking)
  const comparisonRows = useMemo(() => {
    if (!currentTurn) return [];

    const groupMap = new Map<
      string,
      { base: string; current?: VariantBlock; minimal?: VariantBlock; none?: VariantBlock }
    >();

    variants.forEach((c) => {
      const turn = c.turns?.find((t) => t.turnIdx === currentTurn.turnIdx);
      turn?.modelResponses?.forEach((r) => {
        const tv = detectTrainVariant(r.modelName);
        const key = baseModelKey(r.modelName);
        const row = groupMap.get(key) ?? { base: key };
        const block: VariantBlock = { response: r.response, id: r.id, turnId: turn!.id, modelName: r.modelName };
        if (tv === 'current') row.current = block;
        if (tv === 'minimal') row.minimal = block;
        if (tv === 'none') row.none = block;
        groupMap.set(key, row);
      });
    });

    return Array.from(groupMap.values()).sort((a, b) => a.base.localeCompare(b.base));
  }, [currentTurn, variants]);

  const allBaseNames = useMemo(() => comparisonRows.map((r) => r.base), [comparisonRows]);
  const defaultRankingBases = ['Qwen2.5-7B Patient LoRA', 'MedGemma-4B Patient LoRA'];
  const visibleBasesDefault = useMemo(() => {
    const preferred = new Set(allBaseNames.filter((b) => defaultRankingBases.includes(b)));
    return preferred.size > 0 ? preferred : new Set(allBaseNames);
  }, [allBaseNames.join(',')]);
  const basesToShow = visibleBases.size > 0 ? visibleBases : visibleBasesDefault;
  const filteredComparisonRows = useMemo(
    () => comparisonRows.filter((r) => basesToShow.has(r.base)),
    [comparisonRows, basesToShow]
  );

  const turnIdsInRows = useMemo(() => {
    const ids = new Set<string>();
    comparisonRows.forEach((r) => {
      [r.current, r.minimal, r.none].forEach((b) => { if (b) ids.add(b.turnId); });
    });
    return Array.from(ids);
  }, [comparisonRows]);

  useEffect(() => {
    const load = async () => {
      const next: typeof rankingByTurnId = {};
      for (const turnId of turnIdsInRows) {
        try {
          const res = await apiService.getMyEvalRankings(turnId);
          next[turnId] = {
            best: (res.best || []).map((b) => ({ id: b.id, comment: b.comment || '' })),
            worst: (res.worst || []).map((w) => ({ id: w.id, comment: w.comment || '' })),
          };
        } catch {
          next[turnId] = { best: [], worst: [] };
        }
      }
      setRankingByTurnId((prev) => ({ ...prev, ...next }));
    };
    if (turnIdsInRows.length) load();
  }, [turnIdsInRows.join(',')]);

  const addToBest = async (turnId: string, responseId: string) => {
    const r = rankingByTurnId[turnId] ?? { best: [], worst: [] };
    if (r.best.some((b) => b.id === responseId) || r.worst.some((w) => w.id === responseId)) return;
    if (r.best.length >= 2) return;
    const best = [...r.best, { id: responseId, comment: '' }];
    const worst = r.worst.filter((w) => w.id !== responseId);
    setRankingByTurnId((prev) => ({ ...prev, [turnId]: { best, worst } }));
    try {
      await apiService.submitEvalRankings(turnId, { best, worst });
    } catch (_) {}
  };

  const addToWorst = async (turnId: string, responseId: string) => {
    const r = rankingByTurnId[turnId] ?? { best: [], worst: [] };
    if (r.best.some((b) => b.id === responseId) || r.worst.some((w) => w.id === responseId)) return;
    if (r.worst.length >= 2) return;
    const worst = [...r.worst, { id: responseId, comment: '' }];
    const best = r.best.filter((b) => b.id !== responseId);
    setRankingByTurnId((prev) => ({ ...prev, [turnId]: { best, worst } }));
    try {
      await apiService.submitEvalRankings(turnId, { best, worst });
    } catch (_) {}
  };

  const removeFromBest = async (turnId: string, responseId: string) => {
    const r = rankingByTurnId[turnId];
    if (!r) return;
    const best = r.best.filter((b) => b.id !== responseId);
    setRankingByTurnId((prev) => ({ ...prev, [turnId]: { ...r, best } }));
    try {
      await apiService.submitEvalRankings(turnId, { best, worst: r.worst });
    } catch (_) {}
  };

  const removeFromWorst = async (turnId: string, responseId: string) => {
    const r = rankingByTurnId[turnId];
    if (!r) return;
    const worst = r.worst.filter((w) => w.id !== responseId);
    setRankingByTurnId((prev) => ({ ...prev, [turnId]: { ...r, worst } }));
    try {
      await apiService.submitEvalRankings(turnId, { best: r.best, worst });
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-blue-50">
      <div className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-purple-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg shadow-purple-200">
                <SparklesIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Model Evaluation
                </h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Compare and rank AI model responses — current vs minimal vs none
                </p>
              </div>
            </div>
            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setSourceFilter('all')}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  sourceFilter === 'all' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All ({cases.length})
              </button>
              <button
                onClick={() => setSourceFilter('gold')}
                className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-1.5 ${
                  sourceFilter === 'gold' ? 'bg-white text-yellow-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <StarIconSolid className="h-4 w-4 text-yellow-500" />
                Gold
              </button>
              <button
                onClick={() => setSourceFilter('synthetic')}
                className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-1.5 ${
                  sourceFilter === 'synthetic' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BeakerIcon className="h-4 w-4 text-blue-500" />
                Synthetic
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 flex items-center gap-3">
            {error}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Left: Case list */}
          <div className="w-72 flex-shrink-0">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-28">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-gray-800">Cases</h2>
              </div>
              {isLoading ? (
                <div className="p-8 text-center text-gray-500">Loading...</div>
              ) : logicalCases.length === 0 ? (
                <div className="p-8 text-center text-gray-500">No cases</div>
              ) : (
                <div className="divide-y divide-gray-50 max-h-[calc(100vh-260px)] overflow-y-auto">
                  {logicalCases.map((logical) => (
                    <button
                      key={`${logical.sourceType}-${logical.sampleId}`}
                      onClick={() => loadLogicalCase(logical)}
                      className={`w-full text-left px-4 py-3 hover:bg-purple-50 transition-all ${
                        selectedLogical?.sourceType === logical.sourceType && selectedLogical?.sampleId === logical.sampleId
                          ? 'bg-purple-50 border-l-4 border-purple-500'
                          : 'border-l-4 border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        {logical.sourceType === 'gold' ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                            Gold
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            Synthetic
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-900 truncate">{logical.title}</p>
                      <p className="text-xs text-gray-500">
                        {selectedLogical?.sourceType === logical.sourceType && selectedLogical?.sampleId === logical.sampleId && variants.length
                          ? `${variants.length} variants`
                          : ''}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Main: two-panel layout when case + turn selected */}
          <div className="flex-1 min-w-0">
            {!selectedLogical ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
                <ChatBubbleLeftRightIcon className="h-12 w-12 text-purple-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900">Select a case</h3>
                <p className="mt-2 text-gray-500">Choose a case to view turns and compare model responses by prompt variant.</p>
              </div>
            ) : isCaseLoading ? (
              <div className="bg-white rounded-2xl shadow-sm border p-16 text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-purple-200 border-t-purple-600 mx-auto" />
                <p className="mt-4 text-gray-500">Loading case...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Case header */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4 flex items-center gap-3">
                    {selectedLogical.sourceType === 'gold' ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                        Gold
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                        Synthetic
                      </span>
                    )}
                    <h2 className="text-lg font-bold text-gray-900">{selectedLogical.title}</h2>
                  </div>
                </div>

                {/* System prompts for all variants */}
                {variants.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <button
                      onClick={() => setShowSystemPrompts(!showSystemPrompts)}
                      className="w-full px-4 py-3 bg-purple-50 border-b border-purple-100 hover:bg-purple-100 transition-colors flex items-center justify-between"
                    >
                      <h3 className="text-sm font-semibold text-purple-800">System prompts by context level</h3>
                      <ChevronDownIcon className={`h-4 w-4 text-purple-800 transition-transform ${showSystemPrompts ? 'rotate-180' : ''}`} />
                    </button>
                    {showSystemPrompts && (
                      <div className="divide-y divide-gray-100">
                        {(() => {
                          type PromptBlock = { id: string; label: string; text: string };
                          const blocks: PromptBlock[] = [];
                          // DB uses 'v2' for full-context prompt; we always label as "Rich info" (not "v2") to avoid confusion with model version
                          const richVariant = variants.find((v) => v.promptVariant === 'v2');
                          const minimalNoneVariant = variants.find((v) => v.promptVariant === 'minimal_none');
                          if (richVariant) {
                            blocks.push({ id: richVariant.id, label: 'Rich info', text: richVariant.systemPrompt });
                          }
                          if (minimalNoneVariant) {
                            blocks.push({
                              id: `${minimalNoneVariant.id}-minimal`,
                              label: 'Minimal info',
                              text: 'Minimal context: name, age, relationship, and similar basic demographic information only (no clinical or scenario details).',
                            });
                            blocks.push({
                              id: `${minimalNoneVariant.id}-none`,
                              label: 'Zero info',
                              text: minimalNoneVariant.systemPrompt,
                            });
                          }
                          return blocks.map((block) => {
                            const isExpanded = expandedSystemPromptId === block.id;
                            const isLongPrompt = block.text.length > 500;
                            return (
                              <div key={block.id} className="p-4">
                                <button
                                  onClick={() => isLongPrompt && setExpandedSystemPromptId(isExpanded ? null : block.id)}
                                  className={`w-full flex items-center justify-between gap-2 mb-2 ${isLongPrompt ? 'hover:bg-gray-50 -mx-2 px-2 py-1 rounded transition-colors cursor-pointer' : ''}`}
                                >
                                  <span className="text-xs font-semibold text-gray-700">{block.label}</span>
                                  {isLongPrompt && (
                                    <ChevronDownIcon className={`h-3 w-3 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                  )}
                                </button>
                                <div className={`p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap border border-gray-100 ${isLongPrompt && !isExpanded ? 'line-clamp-6' : ''}`}>
                                  {block.text}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    )}
                  </div>
                )}

                {/* Rubric */}
                <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h3 className="font-semibold text-slate-700">Rubric suggestions</h3>
                    <button
                      onClick={saveFeedback}
                      disabled={isSavingFeedback}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-slate-600 rounded-lg hover:bg-slate-700 disabled:opacity-50"
                    >
                      {isSavingFeedback ? 'Saving...' : feedbackSaveMessage || 'Save'}
                    </button>
                  </div>
                  <textarea
                    value={rubricSuggestions}
                    onChange={(e) => setRubricSuggestions(e.target.value)}
                    placeholder="What criteria matter for judging responses?"
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-slate-400 bg-white"
                  />
                </div>

                {/* Turn selector */}
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm font-medium text-gray-700 self-center">Turn:</span>
                  {turns.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedTurnIdx(t.turnIdx)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedTurnIdx === t.turnIdx
                          ? 'bg-purple-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-700 hover:bg-purple-50'
                      }`}
                    >
                      {t.turnIdx}
                    </button>
                  ))}
                </div>

                {currentTurn && (
                  <div className="flex gap-4">
                    {/* Context panel: conversation so far */}
                    <div className="w-80 flex-shrink-0">
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden sticky top-28">
                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                          <h3 className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Context so far</h3>
                        </div>
                        <div className="p-3 max-h-[calc(100vh-320px)] overflow-y-auto space-y-3">
                          {previousTurns.map((t) => (
                            <div key={t.id} className="space-y-1.5 text-xs">
                              <p className="font-semibold text-blue-600">Turn {t.turnIdx} — Clinician</p>
                              <p className="text-gray-600 leading-relaxed line-clamp-3">{t.doctorMessage}</p>
                              <p className="font-semibold text-emerald-600">Expected</p>
                              <p className="text-gray-600 leading-relaxed line-clamp-2">{t.expectedResponse}</p>
                            </div>
                          ))}
                          <div className="pt-2 border-t border-gray-100">
                            <p className="font-semibold text-blue-600">Turn {currentTurn.turnIdx} — Clinician (current)</p>
                            <p className="text-gray-700 leading-relaxed text-sm mt-1">{currentTurn.doctorMessage}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Main: expected + model-centric table */}
                    <div className="flex-1 min-w-0 space-y-4">
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100">
                          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Expected response</p>
                        </div>
                        <div className="p-4">
                          <p className="text-gray-800 leading-relaxed text-sm">{currentTurn.expectedResponse}</p>
                        </div>
                      </div>

                      {/* Choose models (top): filters which comparison cards show below; default 2 models */}
                      {displayCase && allBaseNames.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                              <h3 className="text-sm font-semibold text-amber-800">Choose models</h3>
                            </div>
                            <button
                              type="button"
                              onClick={() => setShowModelList(!showModelList)}
                              className="mt-2 text-xs font-medium text-amber-700 hover:text-amber-900 flex items-center gap-1"
                            >
                              {showModelList ? 'Hide' : 'Show'} model selection
                              <ChevronDownIcon className={`h-4 w-4 transition-transform ${showModelList ? 'rotate-180' : ''}`} />
                            </button>
                            {showModelList && (
                              <div className="flex flex-wrap gap-3 pt-3 mt-2 border-t border-amber-100">
                                {allBaseNames.map((base) => {
                                  const isChecked = basesToShow.has(base);
                                  return (
                                    <label key={base} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => {
                                          const current = new Set(basesToShow);
                                          if (current.has(base)) current.delete(base);
                                          else current.add(base);
                                          if (current.size === 0) allBaseNames.forEach((b) => current.add(b));
                                          setVisibleBases(current);
                                        }}
                                        className="rounded text-amber-600 focus:ring-amber-500"
                                      />
                                      <span className="text-xs text-amber-900">{base}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* One card per base: rich info + minimal info + zero info (filtered by model selection; default 2 models) */}
                      <div className="space-y-6">
                        {filteredComparisonRows.length === 0 && (
                          <p className="text-sm text-gray-500">
                            {comparisonRows.length === 0 ? 'No model responses for this turn.' : 'Select at least one model above.'}
                          </p>
                        )}
                        {filteredComparisonRows.map((row) => (
                          <div
                            key={row.base}
                            className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
                          >
                            <div className="px-4 py-3 bg-purple-50 border-b border-purple-100">
                              <h3 className="text-sm font-bold text-purple-900">{row.base}</h3>
                            </div>
                            <div className="divide-y divide-gray-100">
                              {[row.current, row.minimal, row.none].filter(Boolean).map((block) => {
                                const inBest = (rankingByTurnId[block!.turnId]?.best ?? []).some((b) => b.id === block!.id);
                                const inWorst = (rankingByTurnId[block!.turnId]?.worst ?? []).some((w) => w.id === block!.id);
                                const isUsed = inBest || inWorst;
                                const isExpanded = expandedComparisonId === block!.id;
                                const variant = detectTrainVariant(block!.modelName);
                                const displayModelName = baseModelKey(block!.modelName);
                                const infoLabel = variant === 'current' ? 'Rich info' : variant === 'minimal' ? 'Minimal info' : 'Zero info';
                                const infoLabelColor = variant === 'current' ? 'bg-blue-100 text-blue-800 border-blue-200' : variant === 'minimal' ? 'bg-yellow-100 text-yellow-800 border-yellow-200' : 'bg-gray-100 text-gray-800 border-gray-200';
                                const colors = getModelColor(displayModelName);
                                const bestLen = (rankingByTurnId[block!.turnId]?.best ?? []).length;
                                const worstLen = (rankingByTurnId[block!.turnId]?.worst ?? []).length;
                                return (
                                  <div
                                    key={block!.id}
                                    className={`p-4 transition-all ${isUsed ? 'opacity-75 bg-gray-50/50' : ''}`}
                                  >
                                    <div className="flex items-start justify-between gap-4">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                                          <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold ${colors.bg} ${colors.text} border ${colors.border}`}>
                                            {displayModelName}
                                          </span>
                                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${infoLabelColor} border`}>
                                            {infoLabel}
                                          </span>
                                          {RANKING_SECTION_ENABLED && inBest && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                                              Best
                                            </span>
                                          )}
                                          {RANKING_SECTION_ENABLED && inWorst && (
                                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                                              Worst
                                            </span>
                                          )}
                                        </div>
                                        <p className={`text-sm text-gray-700 leading-relaxed whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-3'}`}>
                                          {block!.response}
                                        </p>
                                        <button
                                          type="button"
                                          onClick={() => setExpandedComparisonId(isExpanded ? null : block!.id)}
                                          className="mt-2 text-xs font-medium text-purple-600 hover:text-purple-800"
                                        >
                                          {isExpanded ? 'Show less' : 'Show more'}
                                        </button>
                                      </div>
                                      {RANKING_SECTION_ENABLED && !isUsed && (
                                        <div className="flex flex-col gap-2 flex-shrink-0">
                                          <button
                                            type="button"
                                            onClick={() => addToBest(block!.turnId, block!.id)}
                                            disabled={bestLen >= 2}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 rounded-xl hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                          >
                                            <HandThumbUpIcon className="h-4 w-4" />
                                            Best
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => addToWorst(block!.turnId, block!.id)}
                                            disabled={worstLen >= 2}
                                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                          >
                                            <HandThumbDownIcon className="h-4 w-4" />
                                            Worst
                                          </button>
                                        </div>
                                      )}
                                      {RANKING_SECTION_ENABLED && isUsed && (
                                        <div className="flex flex-col gap-2 flex-shrink-0">
                                          {inBest && (
                                            <button
                                              type="button"
                                              onClick={() => removeFromBest(block!.turnId, block!.id)}
                                              className="text-xs font-medium text-green-600 hover:text-green-800"
                                            >
                                              Remove from Best
                                            </button>
                                          )}
                                          {inWorst && (
                                            <button
                                              type="button"
                                              onClick={() => removeFromWorst(block!.turnId, block!.id)}
                                              className="text-xs font-medium text-red-600 hover:text-red-800"
                                            >
                                              Remove from Worst
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Rank responses list (hidden when disabled) */}
                      {displayCase && RANKING_SECTION_ENABLED && (
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                          <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
                            <h3 className="text-sm font-semibold text-amber-800">Rank responses</h3>
                          </div>
                          <div className="p-4">
                            {(() => {
                              const turn = displayCase?.turns?.find((t) => t.turnIdx === currentTurn.turnIdx);
                              if (!turn?.modelResponses?.length) return <p className="text-gray-500 text-sm">No responses for this turn.</p>;
                              const filtered =
                                basesToShow.size > 0
                                  ? turn.modelResponses.filter((r) => basesToShow.has(baseModelKey(r.modelName)))
                                  : turn.modelResponses;
                              if (filtered.length === 0) return <p className="text-gray-500 text-sm">Select at least one model above to include in ranking.</p>;
                              return (
                                <ResponseRanker
                                  turnId={turn.id}
                                  modelResponses={filtered}
                                  onSaved={() => handleRankingSaved(turn.id)}
                                />
                              );
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedLogical && variants.length > 0 && selectedTurnIdx == null && (
                  <p className="text-gray-500 text-sm">Select a turn above to view context and compare responses.</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationPage;
